import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type ReactNode } from "react";

import { Icon } from "../Icon";
import { Text } from "../Text";

export interface ChipProps {
  label: string;
  onRemove?: () => void;
  disabled?: boolean;
  /** Content to render before the label (e.g., avatar) */
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * A base chip component for displaying a label with optional remove action.
 * Use `children` prop to add content before the label (e.g., avatar).
 */
export function Chip(props: ChipProps) {
  const { label, onRemove, disabled = false, children, sx } = props;

  const isRemovable = Boolean(onRemove) && !disabled;

  return (
    <Box
      sx={mergeSxProps(
        (theme: Theme) => ({
          display: "inline-flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "5px",
          paddingTop: "2px",
          paddingBottom: "2px",
          paddingLeft: children ? "2px" : "5px",
          paddingRight: "5px",
          backgroundColor: theme.palette.background.muted,
          borderRadius: theme.borderRadius?.large,
          opacity: disabled ? theme.opacity?.disabled : undefined,
        }),
        sx
      )}
    >
      {children}
      <Text
        sx={(theme) => ({
          color: disabled ? theme.palette.text.disabled : theme.palette.text.secondary,
          maxWidth: 120,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        })}
        variant="caption"
      >
        {label}
      </Text>
      {isRemovable && (
        <Box
          aria-label={`Remove ${label}`}
          component="button"
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            border: "none",
            backgroundColor: "transparent",
            cursor: "pointer",
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary,
            },
          })}
          onClick={onRemove}
        >
          <Icon size="small" type="close" />
        </Box>
      )}
    </Box>
  );
}
